import { faCircle as lightCircle } from "@jewlr/pro-light-svg-icons/faCircle"
import { faCheckCircle as solidCheckCircle } from "@jewlr/pro-solid-svg-icons/faCheckCircle"
import Button from "@jewlr/storybook/core/button"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import styled from "styled-components"

import { fetchInventory, updateChoice } from "areas/product/store/actions"
import { Box, Flex, Typography } from "components/common"
import FontAwesomeIcon from "components/font-awesome-icon"
import Modal, { closeModal } from "components/modal"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptions } from "helpers/product/product-filtering"

const TableWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.grey4};
  margin: 20px 0;
  overflow-x: auto;
  padding: 0 10px;
  width: 100%;
  ${(props) => props.theme.mediaQueries.tablet`
    padding: 10px 40px;
  `}
`
const Table = styled.table`
  width: 100%;
  tr {
    border-bottom: 1px solid ${(props) => props.theme.colors.grey10};
  }
  tbody tr:last-child {
    border-bottom: none;
  }
  th,
  td {
    padding: 10px;
    text-align: center;
    vertical-align: center;
    :first-child {
      padding-left: 0;
      text-align: left;
    }
  }
  th {
    color: ${(props) => props.theme.colors.black};
  }
  td {
    font-weight: 300;
  }
`
const TableIcon = styled.span`
  color: ${(props) =>
    props.checked
      ? props.theme.colors.greenDarker
      : props.theme.colors.grey[20]};
  cursor: pointer;
  font-size: 20px;
`
const SelectLabel = styled.label`
  display: block;
  margin-bottom: 5px;
`
const InternalInventoryTableModal = ({
  getAllInventory,
  handleChange,
  inventory,
  selectedMetalFilter,
  selectedSize,
  selectedStoneCount,
  setChoice,
  sizes,
  skus,
  stoneCounts,
}) => {
  return (
    <Modal label={`${sl.config.guaranteed_holiday} Delivery`} show="#inventory">
      {inventory && (
        <>
          <Box px="14px" textAlign="center">
            <Box
              as="img"
              mb="15px"
              src={require("images/layout/footer/perks/freeshipping.svg")}
              width="40px"
            />
            <Typography color="black" fontSize="14px" gutterbottom>
              This item in select metals{!!sizes && " and sizes"}
              <br />
              is available for {sl.config.guaranteed_holiday} Delivery.
            </Typography>

            <Typography mb={1}>
              Select a configuration below to update your item. Configurations
              marked in green are available for{" "}
              <span className="nowrap">
                {sl.config.guaranteed_holiday} Delivery.
              </span>
            </Typography>
            {!!sizes && (
              <Box mb="10px">
                Choose your size:{" "}
                <select
                  onChange={(e) => handleChange(e, "sz")}
                  value={selectedSize}
                >
                  {filterProductOptions(
                    sizes.options,
                    selectedMetalFilter,
                    true
                  ).map((sz, index) => (
                    <option key={index} value={sz.value}>
                      {sz.value}
                    </option>
                  ))}
                </select>
              </Box>
            )}
            {inventory.showStoneCount && !!stoneCounts && (
              <Box my="10px">
                <SelectLabel>How many stones would you like?</SelectLabel>
                <select
                  onChange={(e) => handleChange(e, "sc")}
                  value={selectedStoneCount}
                >
                  {stoneCounts.options.map((sc, index) => (
                    <option key={index} value={sc.value}>
                      {sc.value}
                    </option>
                  ))}
                </select>
              </Box>
            )}
          </Box>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  {inventory.attributes.map((attribute, index) => (
                    <th key={index}>{attributeName(attribute)}</th>
                  ))}
                  {skus.map((sku, index) => (
                    <th key={index}>
                      <Box
                        alt={sku.title}
                        as="img"
                        src={imageLookup("sku", sku.value).x1}
                        title={sku.title}
                        width={{ _: "25px", tablet: "35px" }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inventory.available_inventories.map((inv, index) => {
                  const attributes = inv.item_attributes_hash
                  return (
                    <tr key={index}>
                      {inventory.attributes.map((attribute, index) => (
                        <td
                          dangerouslySetInnerHTML={{
                            __html: attributeName(attributes[attribute]),
                          }}
                          key={index}
                        />
                      ))}
                      {Object.keys(inv.sku_availability_hash).map(
                        (sku, index) => (
                          <td
                            key={"s" + index}
                            onClick={() => {
                              setChoice(sku, attributes)
                              closeModal()
                            }}
                          >
                            {inv.sku_availability_hash[sku]
                              .available_holiday ? (
                              <TableIcon checked>
                                <FontAwesomeIcon icon={solidCheckCircle} />
                              </TableIcon>
                            ) : (
                              <TableIcon>
                                <FontAwesomeIcon icon={lightCircle} />
                              </TableIcon>
                            )}
                          </td>
                        )
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableWrapper>
          {!!sizes && (
            <Box textAlign="center">
              <Button
                color="primary"
                fontWeight="bold"
                mb="15px"
                onClick={() => getAllInventory()}
              >
                Show All Sizes
              </Button>
            </Box>
          )}
        </>
      )}
    </Modal>
  )
}

const attributeName = (attribute) => {
  switch (attribute) {
    case "sz":
      return "Size"
    case "as":
      return "Accents"
    case "sc":
      return "Stone Count"
    case "CZ":
      return "Cubic<br />Zirconia"
    case "DIA":
      return "Genuine<br />Diamond"
    default:
      return attribute
  }
}
const getSelectedMetalFilter = (state) => {
  if (typeof state.choices.sku === "undefined") {
    return ""
  } else {
    // return state.choices.sku.filter || ''
    return {
      pref: "sku",
      value: state.choices.sku.value,
    }
  }
}

const mapStateToProps = (state) => {
  return {
    inventory: state.wizard.inventory,
    selectedMetalFilter: getSelectedMetalFilter(state),
    selectedSize: state.choices.sz ? state.choices.sz.value || "" : "",
    selectedStoneCount: state.choices.sc ? state.choices.sc.value || "" : "",
    sizes: state.wizard.data && state.wizard.data.sz,
    skus:
      state.wizard.data && state.wizard.data.sku
        ? state.wizard.data.sku.options
        : [],
    stoneCounts: state.wizard.data.sc,
    wizardData: state.wizard.data,
  }
}

const mapDispatchToProps = (dispatch) => {
  return { dispatch }
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { wizardData } = stateProps
  const { dispatch } = dispatchProps
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    getAllInventory: () => {
      dispatch(fetchInventory({ get_all_sizes: true }))
    },
    handleChange: (e, pref) => {
      // Find the object to send directly to updateChoice
      const val = e.target.value
      if (val == "") {
        dispatch(updateChoice(pref, { value: "" }))
      } else {
        let selectedValue = wizardData[pref].options.find(
          (option) => option.value == val
        )
        try {
          dispatch(updateChoice(pref, selectedValue))
        } catch (err) {
          console.log(err)
        }
      }
      dispatch(fetchInventory())
    },
    setChoice: (sku, attributes) => {
      // Update metal choice
      let selectedMetal = wizardData.sku.options.find(
        (metal) => metal.value == sku
      )
      dispatch(updateChoice("sku", selectedMetal))

      // Update each attribute choice
      for (let attribute in attributes) {
        let attributeValue = attributes[attribute]
        if (attribute === "as") {
          attributeValue = attributeValue == "CZ" ? "04CZIR" : "04DIAM"
        }
        let selectedValue = wizardData[attribute].options.find(
          (option) => option.value == attributeValue
        )
        dispatch(updateChoice(attribute, selectedValue))
      }
    },
  }
}

InternalInventoryTableModal.propTypes = {
  getAllInventory: PropTypes.func,
  handleChange: PropTypes.func,
  inventory: PropTypes.object,
  routerHash: PropTypes.string,
  selectedMetalFilter: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  selectedSize: PropTypes.string,
  selectedStoneCount: PropTypes.string,
  setChoice: PropTypes.func,
  sizes: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  skus: PropTypes.array,
  stoneCounts: PropTypes.object,
}

const InventoryTableModal = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(InternalInventoryTableModal)

export default InventoryTableModal
